import {
  doRRDPrintTypesMatch,
  normalizeRRDPrintTypeContext,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";
import { resolveRRDStoreCopyConfig } from "@/features/campaigns/direct-mail/rrd/utils/rrdStoreCopyConfigHelper";
import { validateNationalPlusUpStoreCopyQuantity } from "../../utils/nationalPlusUpHelpers";

export const NATIONAL_PLUS_UP_BULK_PRINT_TYPE_ERROR =
  "Select a print type for store copy.";
export const NATIONAL_PLUS_UP_BULK_PRINT_TYPE_LOCATION_ERROR =
  "Select at least one selected location for the chosen print type.";
export const NATIONAL_PLUS_UP_BULK_INCOMPATIBLE_CONFIG_ERROR =
  "Selected locations do not share a valid store copy quantity range.";

type LocationId = string | number;

type LocationValue = {
  location_id?: LocationId;
  id?: LocationId;
  print_type?: string;
  print_type_display?: string;
  print_type_label?: string;
  store_copy_cpm?: number | string;
  store_copy_min_quantity?: number | string;
  store_copy_max_quantity?: number | string;
};

type LocationValues = Record<string, LocationValue | unknown>;

type BulkValues = {
  distribution?: boolean;
  store_copy?: boolean;
  store_copy_qnt?: number | string;
  print_type?: string;
};

type StoreCopyConfig = {
  cpm: number;
  minQuantity: number;
  maxQuantity: number;
  isConfigured: boolean;
  source?: string;
};

function getLocationValue(
  locationValues: LocationValues = {},
  locationId: LocationId
): LocationValue | null {
  const value = locationValues[String(locationId)];
  return isLocationValue(value) ? value : null;
}

function isLocationValue(value: unknown): value is LocationValue {
  return Boolean(value && typeof value === "object" && "print_type" in value);
}

function getLocationsForBulkOptions({
  locationValues = {},
}: {
  checkedLocations?: LocationId[];
  locationValues?: LocationValues;
}): LocationValue[] {
  return Object.values(locationValues).filter(isLocationValue);
}

function getLocationsForBulkConfig({
  checkedLocations = [],
  locationValues = {},
}: {
  checkedLocations?: LocationId[];
  locationValues?: LocationValues;
}): LocationValue[] {
  return getLocationsForBulkOptions({ checkedLocations, locationValues });
}

export function getNationalPlusUpBulkPrintTypeOptions({
  checkedLocations = [],
  locationValues = {},
}: {
  checkedLocations?: LocationId[];
  locationValues?: LocationValues;
}) {
  const optionByLabel = new Map<string, { label: string; value: string }>();

  getLocationsForBulkOptions({ checkedLocations, locationValues }).forEach(
    (location) => {
      const printTypeContext = normalizeRRDPrintTypeContext(location);
      const label = printTypeContext.printTypeLabel;
      const value = printTypeContext.printType;

      if (!label || !value || optionByLabel.has(label)) {
        return;
      }

      optionByLabel.set(label, { label, value });
    }
  );

  return [...optionByLabel.values()];
}

export function getNationalPlusUpMatchingBulkLocationIds({
  checkedLocations = [],
  locationValues = {},
  printType = "",
  storeCopy = false,
}: {
  checkedLocations?: LocationId[];
  locationValues?: LocationValues;
  printType?: string;
  storeCopy?: boolean;
}) {
  if (!storeCopy) {
    return checkedLocations;
  }

  if (!printType) {
    return [];
  }

  return checkedLocations.filter((locationId) => {
    const location = getLocationValue(locationValues, locationId);
    return doRRDPrintTypesMatch(location?.print_type, printType);
  });
}

export function getNationalPlusUpBulkStoreCopyConfig({
  checkedLocations = [],
  locationValues = {},
  printType = "",
}: {
  checkedLocations?: LocationId[];
  locationValues?: LocationValues;
  printType?: string;
}): StoreCopyConfig | null {
  if (!printType) {
    return null;
  }

  const selectedConfigs = getLocationsForBulkConfig({
    checkedLocations,
    locationValues,
  })
    .filter((location) => doRRDPrintTypesMatch(location.print_type, printType))
    .map((location) =>
      resolveRRDStoreCopyConfig({
        location,
      })
    )
    .filter((config) => config?.isConfigured) as StoreCopyConfig[];

  if (!selectedConfigs.length) {
    return null;
  }

  const [firstConfig] = selectedConfigs;
  const allMatch = selectedConfigs.every(
    (config) =>
      config.cpm === firstConfig.cpm &&
      config.minQuantity === firstConfig.minQuantity &&
      config.maxQuantity === firstConfig.maxQuantity
  );

  if (allMatch) {
    return firstConfig;
  }

  const minQuantity = Math.max(
    ...selectedConfigs.map((config) => config.minQuantity)
  );
  const maxQuantity = Math.min(
    ...selectedConfigs.map((config) => config.maxQuantity)
  );

  return {
    cpm: firstConfig.cpm,
    minQuantity,
    maxQuantity,
    isConfigured: minQuantity <= maxQuantity,
    source:
      minQuantity <= maxQuantity
        ? "national-plus-up-bulk-intersection"
        : "national-plus-up-bulk-incompatible",
  };
}

export function validateNationalPlusUpBulkSelection({
  values = {},
  checkedLocations = [],
  locationValues = {},
}: {
  values?: BulkValues;
  checkedLocations?: LocationId[];
  locationValues?: LocationValues;
}) {
  const errors: string[] = [];

  if (!values.distribution && !values.store_copy) {
    errors.push("Select Plus-Up Distribution or Store Copy.");
  }

  if (!values.store_copy) {
    return errors;
  }

  if (!values.print_type) {
    errors.push(NATIONAL_PLUS_UP_BULK_PRINT_TYPE_ERROR);
    return errors;
  }

  const matchingLocationIds = getNationalPlusUpMatchingBulkLocationIds({
    checkedLocations,
    locationValues,
    printType: values.print_type,
    storeCopy: true,
  });

  if (checkedLocations.length && !matchingLocationIds.length) {
    errors.push(NATIONAL_PLUS_UP_BULK_PRINT_TYPE_LOCATION_ERROR);
    return errors;
  }

  const storeCopyConfig = getNationalPlusUpBulkStoreCopyConfig({
    checkedLocations,
    locationValues,
    printType: values.print_type,
  });

  if (storeCopyConfig?.source === "national-plus-up-bulk-incompatible") {
    errors.push(NATIONAL_PLUS_UP_BULK_INCOMPATIBLE_CONFIG_ERROR);
    return errors;
  }

  const storeCopyError = validateNationalPlusUpStoreCopyQuantity(
    values.store_copy_qnt,
    values.store_copy,
    storeCopyConfig || undefined
  );

  if (storeCopyError) {
    errors.push(storeCopyError);
  }

  return errors;
}
